/**
 * OfflineIndicator - 오프라인 상태 표시
 *
 * 네트워크 상태와 대기 중인 동기화 작업을 표시
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, CloudOff, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { useOffline } from '../../hooks/useOffline';

interface OfflineIndicatorProps {
  className?: string;
  showPendingCount?: boolean;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  className = '',
  showPendingCount: _showPendingCount = true,
}) => {
  const { isOnline, pendingCount, isSyncing, lastSyncResult, syncNow } = useOffline();

  // 모든 것이 정상이면 렌더링하지 않음
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 ${className}`}
      >
        <div
          className={`
            flex items-center gap-3 px-4 py-2 rounded-full shadow-lg backdrop-blur-md
            ${!isOnline
              ? 'bg-[var(--danger)]/90 text-white'
              : isSyncing
              ? 'bg-[var(--info)]/90 text-white'
              : pendingCount > 0
              ? 'bg-[var(--warning)]/90 text-black'
              : 'bg-[var(--success)]/90 text-white'
            }
          `}
        >
          {/* 아이콘 */}
          {!isOnline ? (
            <WifiOff size={18} />
          ) : isSyncing ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <RefreshCw size={18} />
            </motion.div>
          ) : pendingCount > 0 ? (
            <CloudOff size={18} />
          ) : (
            <Check size={18} />
          )}

          {/* 메시지 */}
          <span className="text-sm font-medium">
            {!isOnline
              ? '오프라인 모드'
              : isSyncing
              ? '동기화 중...'
              : pendingCount > 0
              ? `${pendingCount}개 기록 대기 중`
              : '동기화 완료'}
          </span>

          {/* 동기화 버튼 (온라인 + 대기 중인 기록 있을 때) */}
          {isOnline && pendingCount > 0 && !isSyncing && (
            <motion.button
              onClick={syncNow}
              className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="지금 동기화"
            >
              <RefreshCw size={14} />
            </motion.button>
          )}
        </div>

        {/* 마지막 동기화 결과 (실패가 있을 때) */}
        {lastSyncResult && lastSyncResult.failed > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--danger)]/20 border border-[var(--danger)]/30"
          >
            <AlertCircle size={14} className="text-[var(--danger)]" />
            <span className="text-xs text-[var(--danger)]">
              {lastSyncResult.failed}개 기록 동기화 실패
            </span>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * OfflineBadge - 작은 오프라인 배지
 */
export const OfflineBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline, pendingCount } = useOffline();

  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className={`
        flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
        ${!isOnline
          ? 'bg-[var(--danger)]/20 text-[var(--danger)]'
          : 'bg-[var(--warning)]/20 text-[var(--warning)]'
        }
        ${className}
      `}
    >
      {!isOnline ? <WifiOff size={12} /> : <CloudOff size={12} />}
      <span>
        {!isOnline ? '오프라인' : `${pendingCount}개 대기`}
      </span>
    </motion.div>
  );
};

export default OfflineIndicator;
